import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Sidebar as ReactProSidebar, Menu } from "react-pro-sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Assignment as WorklistIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  AttachMoney as EarningsIcon,
  Receipt as RateCardIcon,
  Message as MessageIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  Lock as LockIcon
} from "@mui/icons-material";
import Tooltip from '@mui/material/Tooltip';

const StyledSidebar = styled(ReactProSidebar)`
  ${'' /* width: 200px !important; */}
  min-width: 80px !important;
  max-width: 280px !important;
  ${'' /* height: calc(100vh - 104px); */}
  border-radius: 16px;
  background-color: #ffffff;
  box-shadow: 0px 1px 3px 1px #00000026;
  padding: 30px 8px 30px 0px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #000000;

  & > div {
    background-color: #ffffff;
  }
`;

const StyledSidebarContent = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  cursor: ${props => props.locked ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.locked ? '0.5' : '0.7'};
  background-color: transparent;
  border-radius: 10px;
  padding: 0 16px;
  box-sizing: border-box;
  border-left: 10px solid #fff;
  transition: all 1ms ease-out;

  svg {
    color: #000000;
  }
`;

const StyledMenu = styled(Menu)`
  background-color: inherit;
  height: 100%;

  & > ul {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }
`;

const StyledName = styled.div`
  font-size: 16px;
  color: #000000;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
`;

const IconWithLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
`;

const LockIconWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StyledLockIcon = styled(LockIcon)`
  position: absolute;
  right:-20px;
  top: 0px;
  color: #666;
  width:12px!important;
`;

const TooltipContent = styled.div`
  text-align: left;
  padding: 8px;
  background-color: #fff;
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
  font-size: 14px;
`;

const TooltipSubtitle = styled.div`
  font-size: 12px;
  color: black;
`;

const Sidebar = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState(null);

  const menuItems = [
    {
      name: "Worklist",
      icon: <WorklistIcon />,
      path: "/worklist",
      locked: false
    },
    {
      name: "Case History",
      icon: <HistoryIcon />,
      path: "/case-history",
      locked: true
    },
    {
      name: "Analytics",
      icon: <AnalyticsIcon />,
      path: "/analytics",
      locked: true
    },
    {
      name: "Earnings",
      icon: <EarningsIcon />,
      path: "/earnings",
      locked: true
    },
    {
      name: "Rate Card",
      icon: <RateCardIcon />,
      path: "/rate-card",
      locked: true
    },
    {
      name: "Messages",
      icon: <MessageIcon />,
      path: "/messages",
      locked: true
    }
  ];

  const settingsItems = [
    {
      name: "Profile",
      icon: <ProfileIcon />,
      path: "/profile",
      locked: true
    },
    {
      name: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
      locked: true
    },
    {
      name: "Help",
      icon: <HelpIcon />,
      path: "/help",
      locked: true
    },
    {
      name: "Logout",
      icon: <LogoutIcon />,
      path: "/logout",
      locked: true
    }
  ];

  useEffect(() => {
    const currentItem = [...menuItems, ...settingsItems].find(
      (item) => item.path === location.pathname
    );
    if (currentItem) {
      setSelectedItem(currentItem.name);
    }
  }, [location.pathname]);

  const renderMenuItem = (item) => {
    const content = (
      <StyledSidebarContent
        key={item.name}
        isSelected={selectedItem === item.name}
        isSidebarOpen={isSidebarOpen}
        locked={item.locked}
      >
        {isSidebarOpen ? (
          <IconWithLabel>
            {item.icon}
            <StyledName>{item.name}</StyledName>
            {item.locked && <StyledLockIcon />}
          </IconWithLabel>
        ) : (
          <LockIconWrapper>
            {item.icon}
          </LockIconWrapper>
        )}
      </StyledSidebarContent>
    );

    if (item.locked) {
      return (
        <Tooltip
          key={item.name}
          title={
            <TooltipContent>
              <TooltipTitle>Feature Blocked</TooltipTitle>
              <TooltipSubtitle>Explore this feature in Scheduled Demo</TooltipSubtitle>
            </TooltipContent>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'transparent',
                '& .MuiTooltip-arrow': {
                  color: 'transparent'
                }
              }
            }
          }}
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <StyledSidebar collapsed={!isSidebarOpen}>
      <StyledMenu>
        <div>
          {menuItems.map(renderMenuItem)}
        </div>
        <div>
          {settingsItems.map(renderMenuItem)}
        </div>
      </StyledMenu>
    </StyledSidebar>
  );
};

export default Sidebar; 